import {
  Flex,
  formatNumber,
  CurrencyValue,
  LineChart,
  DisplaySwitch,
  ChartSkeleton
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';
import { useApi } from '@/api';
import type {
  SimpleChartData,
  SimpleChartDataset
} from '@blockanalitica/ui/package/types/src/components/charts/SimpleChart';
import { ChartTimeFilter } from '@/components';
import { useChartPrefillDatasetsDates, useChartPeriod } from '@/hooks';

export default function HistoricGraphs({
  underlyingAddress,
  marketSymbol,
  network,
  marketAddress
}: {
  underlyingAddress: string;
  marketSymbol: string;
  network: string;
  marketAddress: string;
}) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<SimpleChartData[]>({
    url: `/markets/${marketAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const [activeDisplayOption, setActiveDisplayOption] =
    useState('total_supply');
  const displayOptions = useMemo(
    () => [
      { key: 'total_supply', value: 'Earn' },
      { key: 'total_borrow', value: 'Borrow' }
    ],
    []
  );

  const yScaleValueFormatter = (value: string | number) =>
    `${formatNumber(Number(value))}`;

  const valueFormatter = (value: string | number) => (
    <CurrencyValue
      value={value}
      variant="dynamic"
      options={{
        currencySize: '3',
        currency: {
          name: marketSymbol,
          address: underlyingAddress,
          network: network
        }
      }}
    />
  );

  const datasets: SimpleChartDataset[] = [
    {
      xField: 'date',
      yField: activeDisplayOption
    }
  ];
  const chartData = useChartPrefillDatasetsDates(data, datasets);

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  if (error) {
    throw error;
  }

  if (!data) {
    return <ChartSkeleton />;
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={chartData}
        config={config}
        datasets={datasets}
        valueFormatter={valueFormatter}
        yScaleValueFormatter={yScaleValueFormatter}
        timeFilter={timeFilter}
      />
    </Flex>
  );
}
